import { objectEntries } from '@trmediaab/zebra-utils';
import type { DefaultTheme } from 'styled-components';
import { createGlobalStyle } from 'styled-components';

import type {
  DarkModeStrategy,
  FontFaceDefinitionLocal,
} from './types/ThemeParts';

export interface GlobalStylesProps {
  theme: DefaultTheme;
  fontBaseUrl: string;
  darkModeStrategy: DarkModeStrategy | undefined;
}

// Inspired by https://github.com/necolas/normalize.css,
// https://hankchizljaw.com/wrote/a-modern-css-reset/ and
// Material UI (https://github.com/mui-org/material-ui/blob/aa4e2dbf3ad98355d2e43cdd281f0ea2c2204046/packages/material-ui/src/CssBaseline/CssBaseline.js#L8)
// A subset of Normalize.css to fix browser inconsistencies
const normalizeBrowsers = `

  html {
    -webkit-text-size-adjust: 100%;
  }

  main, details {
    display: block;
  }

  button,
  input,
  optgroup,
  select,
  textarea {
    font-family: inherit;
    font-size: 100%;
    line-height: inherit;
    color: inherit;
  }

  button,
  input {
    overflow: visible;
  }

  button,
  select {
    text-transform: none;
  }

  button,
  [type="button"],
  [type="reset"],
  [type="submit"] {
    -webkit-appearance: button;
  }

  button {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  button::-moz-focus-inner,
  [type="button"]::-moz-focus-inner,
  [type="reset"]::-moz-focus-inner,
  [type="submit"]::-moz-focus-inner {
    border-style: none;
    padding: 0;
  }

  button:-moz-focusring,
  [type="button"]:-moz-focusring,
  [type="reset"]:-moz-focusring,
  [type="submit"]:-moz-focusring {
    outline: 1px dotted ButtonText;
  }

  textarea {
    overflow: auto;
  }

  [type="number"]::-webkit-inner-spin-button,
  [type="number"]::-webkit-outer-spin-button {
    height: auto;
  }

  [type="search"] {
    -webkit-appearance: textfield;
    outline-offset: -2px;
  }

  ::-webkit-file-upload-button {
    -webkit-appearance: button;
    font: inherit;
  }
`;

// Reset browser defaults
const resetStyles = `

  /* Border box on all elements */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  /* Remove default padding on styled lists. */
  ul,
  ol {
    padding: 0;
  }

  /* Remove default margin */
  body,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  hr,
  p,
  ul,
  ol,
  li,
  figure,
  figcaption,
  blockquote,
  dl,
  dd,
  button,
  input,
  optgroup,
  select,
  textarea {
    margin: 0;
  }

  fieldset {
    border: none;
    margin: 0;
    padding: 0
  }

`;

const defaultStyles = `

  html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    /* Set width in case body is positioned (e.g fixed) */ 
    width: 100%; 
    scroll-behavior: smooth;
  }

  /* Make images easier to work with */
  img {
    max-width: 100%;
    display: block;
  }

  /* Remove list styles on ul, ol elements */
  ul,
  ol {
    list-style: none;
  }

  /* Remove all animations and transitions for people that prefer not to see them */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Save printer ink */
  @media print {
    body {
      background-color: #fff;
    }
  }
`;

/**
 * Inject css variables defined in the theme
 *
 * Dark mode requires:
 *
 * 1. Dark mode color variables in the theme
 * 2. The theme consumer (site) to set the darkModeStrategy prop on ThemeProvider to
 *    either 'system' or an object with a dataAttributeKey property.
 *    The former strategy will use the browser's dark mode setting, the latter will
 *    expect the site to set the `data-${dataAttributeKey}` attribute on the html
 *    element to `dark` when dark mode is enabled.
 *
 * If these requirements are fullfilled we will also opt-in to having the browser
 * use dark colors on form elements and scrollbars.
 *
 */
const variables = ({ theme, darkModeStrategy }: GlobalStylesProps) => {
  const colorVariables = theme.cssVariables?.colors;
  if (colorVariables == null) {
    return null;
  }

  const darkModeStyles =
    darkModeStrategy != null &&
    objectEntries(colorVariables).some(
      ([_key, [, darkModeValue]]) => darkModeValue != null,
    )
      ? `
          color-scheme: dark;
          ${objectEntries(colorVariables)
            .filter(([_key, [, darkModeValue]]) => darkModeValue != null)
            .map(([key, [, darkModeValue]]) => `${key}: ${darkModeValue};`)
            .join('')}
        `
      : null;

  return `
    :root { 
      color-scheme: light;
      ${objectEntries(colorVariables)
        .map(([key, [lightModeValue]]) => `${key}: ${lightModeValue};`)
        .join('')}
    }

    ${
      darkModeStrategy?.strategy === 'dataAttribute'
        ? `
        :root[${darkModeStrategy.dataAttributeKey}='dark'] {
          ${darkModeStyles}
        }
    `
        : ''
    }

    ${
      darkModeStrategy?.strategy === 'system'
        ? `
        @media (prefers-color-scheme: dark) { 
          :root {
            ${darkModeStyles}
          }
        }
    `
        : ''
    }
  `;
};

const fontDeclarations = ({ theme, fontBaseUrl }: GlobalStylesProps) => {
  const { fontFaceDefinitions } = theme;

  if (fontFaceDefinitions == null) {
    return;
  }

  return fontFaceDefinitions
    .filter((fd): fd is FontFaceDefinitionLocal => fd.source === 'local')
    .map((fd: FontFaceDefinitionLocal) => {
      const { familyName, formats, descriptors } = fd;

      return descriptors
        .map(
          d => `
              @font-face {
                font-family: '${familyName}';
                src: ${formats
                  .map(
                    f =>
                      `url('${fontBaseUrl}/${d.filename}.${f}') format('${f}')`,
                  )
                  .join(',')};
                font-weight: ${d.weight};
                font-style: ${d.style};
                font-stretch: ${d.stretch || 'normal'};
                /* https://styled-components.com/docs/faqs#how-do-i-fix-flickering-text-after-server-side-rendering */
                font-display: ${d.display || 'fallback'};
              }
            `,
        )
        .join(' ');
    })
    .join(' ');
};

const GlobalStyles = createGlobalStyle<GlobalStylesProps>`
  ${variables}
  ${normalizeBrowsers}
  ${resetStyles}
  ${defaultStyles}
  ${fontDeclarations}
`;

export default GlobalStyles;
